// src/components/module/ventas/ResumenVenta.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useContext, useRef, useState } from "react";
import {
  Typography,
  Box,
  Divider,
  Button,
  Stack,
  alpha,
  CircularProgress,
} from "@mui/material";
import {
  ShoppingCartCheckout,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  RequestQuote,
  Person as PersonIcon,
  AccountBalanceWallet,
  Discount as DiscountIcon,
  ReceiptLong,
  ArrowForward,
} from "@mui/icons-material";
import { formatMoney } from "../../../utils/format";
import { DarkModeContext } from "../../../context/darkModeContext";

const P = {
  light: {
    bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F",
    textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7",
    accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB",
    warning: "#D9730D", warningBg: "#FADEC9", error: "#E03E3E", errorBg: "#FBE4E4",
    hover: "rgba(55,53,47,0.04)",
  },
  dark: {
    bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6",
    textS: "#9B9A97", textT: "#6B6B6B", border: "#333333",
    accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A",
    warning: "#C77D48", warningBg: "#2E2215", error: "#D44C47", errorBg: "#3D2020",
    hover: "rgba(255,255,255,0.04)",
  },
};

interface ResumenVentaProps {
  venta: {
    id: number;
    cliente?: { id: number; nombre: string };
    numeroFactura?: string;
    subtotal: number;
    descuentoTotal: number;
    impuestos: Array<{ nombre: string; valor: number }>;
    totalAPagar: number;
    articulos?: Array<{ id: number; nombre: string; cantidad: number; precio: number }>;
  };
  /** `temporal` indica que el gesto pidio una factura de prueba (TEMP-). */
  onProcesarPago: (temporal?: boolean) => void;
  onCancelarVenta: (tipo: "cancelacion" | "anulacion") => Promise<void>;
  disabled?: boolean;
  showingPayment?: boolean;
  esCotizacion?: boolean;
  usarFacturaOficial?: boolean;
  holdDurationMs?: number;
}

export const ResumenVenta: React.FC<ResumenVentaProps> = ({
  venta, onProcesarPago, onCancelarVenta, disabled = false,
  showingPayment = false, esCotizacion = false,
  usarFacturaOficial, holdDurationMs = 3000,
}) => {
  const [holdProgress, setHoldProgress] = useState(0);
  const [sosteniendo, setSosteniendo] = useState(false);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const yaDisparado = useRef(false);

  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const handleCancelarVenta = () => {
    onCancelarVenta(esCotizacion ? "cancelacion" : showingPayment ? "anulacion" : "cancelacion");
  };

  // El boton admite dos gestos: pulsacion normal = factura oficial;
  // pulsacion sostenida = factura temporal de prueba (TEMP-).
  const puedeProcesar = !(
    disabled || !venta.cliente || (venta.totalAPagar || 0) === 0 || showingPayment
  );

  const clearHold = () => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    if (progressTimer.current) clearInterval(progressTimer.current);
    holdTimer.current = null;
    progressTimer.current = null;
    setHoldProgress(0);
    setSosteniendo(false);
  };

  const startHold = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!puedeProcesar) return;
    // Capturar el puntero: el gesto se cancelaba en cuanto el cursor se
    // desplazaba unos pixeles fuera del boton, muy facil durante varios segundos.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* no soportado */ }
    yaDisparado.current = false;
    setSosteniendo(true);

    const step = 100 / (holdDurationMs / 50);
    progressTimer.current = setInterval(() => {
      setHoldProgress((prev) => Math.min(prev + step, 100));
    }, 50);

    holdTimer.current = setTimeout(() => {
      // El gesto completa la accion. Antes solo encendia un flag invisible y
      // habia que volver a pulsar el boton para que ocurriera algo.
      yaDisparado.current = true;
      clearHold();
      onProcesarPago(true);
    }, holdDurationMs);
  };

  const endHold = () => {
    const disparado = yaDisparado.current;
    yaDisparado.current = false;
    clearHold();
    if (!disparado && puedeProcesar) onProcesarPago(false);
  };

  const cancelHold = () => {
    yaDisparado.current = false;
    clearHold();
  };

  const LineItem: React.FC<{ label: string; value: string; icon?: React.ReactElement; color?: string; bold?: boolean }> = ({ label, value, icon, color, bold }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.8 }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {icon && <Box sx={{ color: p.textT, "& svg": { fontSize: 15 } }}>{icon}</Box>}
        <Typography sx={{ fontSize: "0.82rem", color: color || p.textS, fontWeight: bold ? 600 : 400 }}>{label}</Typography>
      </Stack>
      <Typography sx={{
        fontSize: bold ? "0.9rem" : "0.84rem", fontWeight: bold ? 700 : 500,
        color: color || p.text, fontFamily: "'IBM Plex Mono', monospace",
      }}>{value}</Typography>
    </Stack>
  );

  const modoTemporal = !esCotizacion && usarFacturaOficial === false;

  return (
    <Box sx={{ position: "relative" }}>
        {/* Cliente */}
        {venta.cliente && (
          <Box sx={{ mb: 3, p: 2, borderRadius: "10px", bgcolor: p.bgS, border: `1px solid ${p.border}` }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <PersonIcon sx={{ fontSize: 14, color: p.textT }} />
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em" }}>Cliente</Typography>
            </Stack>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: p.text }}>{venta.cliente.nombre}</Typography>
            {venta.numeroFactura && (
              <Box sx={{
                mt: 1, display: "inline-flex", alignItems: "center", gap: 0.5,
                px: 1.2, py: 0.3, borderRadius: "6px", fontSize: "0.75rem", fontWeight: 600,
                bgcolor: modoTemporal ? p.warningBg : p.accentBg,
                color: modoTemporal ? p.warning : p.accent,
                transition: "all 0.3s ease",
              }}>
                {esCotizacion ? <RequestQuote sx={{ fontSize: 13 }} /> : <ReceiptIcon sx={{ fontSize: 13 }} />}
                {esCotizacion ? "Cotización" : modoTemporal ? "Factura TEMPORAL" : "Factura"} N° {venta.numeroFactura}
              </Box>
            )}
            {modoTemporal && (
              <Typography sx={{ mt: 0.8, fontSize: "0.72rem", color: p.warning, fontWeight: 500 }}>
                Venta de prueba: no se emite ante la DIAN ni consume numeración oficial.
              </Typography>
            )}
          </Box>
        )}

        {/* Desglose */}
        <Box sx={{ mb: 2 }}>
          <LineItem label="Subtotal" value={formatMoney(venta.subtotal || 0)} icon={<AccountBalanceWallet />} />
          {venta.descuentoTotal > 0 && (
            <LineItem label="Descuento" value={`-${formatMoney(venta.descuentoTotal)}`} icon={<DiscountIcon />} color={p.error} />
          )}
          {venta.impuestos?.map((imp, i) => (
            <LineItem key={i} label={imp.nombre} value={formatMoney(imp.valor)} />
          ))}
        </Box>

        <Divider sx={{ borderColor: p.border, borderStyle: "dashed", my: 2 }} />

        {/* Total */}
        <Box sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.accent}`, bgcolor: p.accentBg, mb: 3 }}>
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>
            Total {esCotizacion ? "Cotizado" : "a Pagar"}
          </Typography>
          <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: p.accent, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "-0.02em" }}>
            {formatMoney(venta.totalAPagar || 0)}
          </Typography>
        </Box>

        {/* Botones */}
        <Stack spacing={1.5}>
          {esCotizacion ? (
            <Button
              variant="contained" fullWidth disableElevation
              startIcon={<RequestQuote sx={{ fontSize: 16 }} />}
              endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
              onClick={() => onProcesarPago(false)}
              disabled={disabled || !venta.cliente || (venta.totalAPagar || 0) === 0 || showingPayment}
              sx={{
                bgcolor: p.accent, color: "#FFF", py: 1.3, fontSize: "0.88rem", fontWeight: 600,
                borderRadius: "8px", "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" },
                "&.Mui-disabled": { bgcolor: alpha(p.accent, 0.15), color: alpha(p.text, 0.3) },
              }}
            >
              Finalizar Cotización
            </Button>
          ) : (
            <Box sx={{ position: "relative" }}>
              <Button
                variant="contained" fullWidth disableElevation
                startIcon={<ShoppingCartCheckout sx={{ fontSize: 16 }} />}
                endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                disabled={disabled || !venta.cliente || (venta.totalAPagar || 0) === 0 || showingPayment}
                onPointerDown={startHold}
                onPointerUp={endHold}
                onPointerCancel={cancelHold}
                onContextMenu={(e) => e.preventDefault()}
                sx={{
                  bgcolor: sosteniendo ? p.warning : p.accent, color: "#FFF",
                  py: 1.3, fontSize: "0.88rem", fontWeight: 600,
                  borderRadius: "8px", userSelect: "none", touchAction: "none",
                  transition: "background-color 0.2s ease",
                  "&:hover": { bgcolor: sosteniendo ? p.warning : darkMode ? "#6BAFDB" : "#1B6FC2" },
                  "&.Mui-disabled": { bgcolor: alpha(p.accent, 0.15), color: alpha(p.text, 0.3) },
                }}
              >
                {sosteniendo ? "Suelta para cancelar…" : "Procesar Pago"}
              </Button>
              {sosteniendo && (
                <CircularProgress
                  variant="determinate"
                  value={holdProgress}
                  size={26}
                  thickness={3}
                  sx={{
                    position: "absolute", top: "50%", right: 14,
                    transform: "translateY(-50%)",
                    color: "rgba(255,255,255,0.9)",
                    pointerEvents: "none",
                  }}
                />
              )}

              {/* El gesto sostenido no se descubre solo: hay que enunciarlo. */}
              {puedeProcesar && (
                <Typography sx={{
                  mt: 0.8, fontSize: "0.72rem", lineHeight: 1.4, textAlign: "center",
                  color: sosteniendo ? p.warning : p.textT,
                  fontWeight: sosteniendo ? 600 : 400,
                }}>
                  {sosteniendo
                    ? `Mantén presionado ${Math.max(0, Math.ceil((holdDurationMs * (100 - holdProgress)) / 100000))} s más para una factura TEMPORAL`
                    : `Mantén presionado ${Math.round(holdDurationMs / 1000)} s para una factura TEMPORAL de prueba`}
                </Typography>
              )}
            </Box>
          )}

          <Button
            fullWidth onClick={handleCancelarVenta}
            disabled={disabled || !venta.cliente}
            startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
            sx={{
              color: p.error, py: 1, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px",
              "&:hover": { bgcolor: p.errorBg },
            }}
          >
            {esCotizacion ? "Cancelar Cotización" : showingPayment ? "Anular Facturación" : "Cancelar Venta"}
          </Button>
        </Stack>

        {/* Hints */}
        {!venta.cliente && !showingPayment && (
          <Box sx={{ mt: 2.5, p: 2, borderRadius: "8px", bgcolor: p.warningBg, border: `1px solid ${alpha(p.warning, 0.2)}` }}>
            <Typography sx={{ fontSize: "0.78rem", color: p.warning, fontWeight: 500 }}>
              Seleccione un cliente para continuar
            </Typography>
          </Box>
        )}
        {venta.articulos && (venta.totalAPagar || 0) === 0 && venta.cliente && !showingPayment && (
          <Box sx={{ mt: 2.5, p: 2, borderRadius: "8px", bgcolor: p.warningBg, border: `1px solid ${alpha(p.warning, 0.2)}` }}>
            <Typography sx={{ fontSize: "0.78rem", color: p.warning, fontWeight: 500 }}>
              Agregue artículos para continuar
            </Typography>
          </Box>
        )}
      </Box>
  );
};
